import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { fromUnixTime, subMinutes } from 'date-fns';
import { Observable, Subject, of, throwError } from 'rxjs';
import { catchError, delay, map, mergeMap, switchMap } from 'rxjs/operators';
import {
  ApiRbacService,
  AppConfig,
  AuthenticationBaseService,
  StorageService,
  UserProfileService,
} from 'sigv-library';
import { UserPermission, UserProfile } from 'sigv-library/model';
import { deepCopy, isNullOrEmpty } from 'sigv-library/util';
import { AdLoginData } from '../models/ad-login-data.model';
import { ApiResponse } from '../models/api-response.model';
import { ApiWebSubmissionService } from './api-web-submission.service';

@Injectable({
  providedIn: 'root',
})
export class ExtranetAuthenticationService extends AuthenticationBaseService {
  readonly authUrl: string = '/';

  constructor(
    protected appConfig: AppConfig,
    protected userProfileService: UserProfileService,
    private storageService: StorageService,
    private apiRbacService: ApiRbacService,
    
    private apiWebSubmissionService: ApiWebSubmissionService,
    private http: HttpClient
  ) {
    super(userProfileService);
    this.authUrl = appConfig.getApiUrl('webSubmission');
    //以下 key 值擴充屬性只有進件會用到，故在此 services 自行擴充 key 值
    (this.storageService.keys as any ).AccessToken = "________acT_____";
    (this.storageService.keys as any).RefreshToken = "________rfT_____";
  }

  /**
   * @description 是否已登入之判斷
   */
  get isAuthenticated(): boolean {
    return (
      !isNullOrEmpty(
        this.storageService.local.getItem(this.storageService.keys.UserProfile)
      ) ||
      !isNullOrEmpty(
        this.storageService.session.getItem(
          this.storageService.keys.UserProfile
        )
      )
    );
  }

  private saveLoginData(data: {
    userProfile: UserProfile;
    tokenObj:{
      idToken:string,
      accessToken: string,
      refreshToken: string
    }
  }): void {
    const keys = this.storageService.keys;
    if (data.userProfile) {
      this.storageService.local.setItem(keys.UserProfile, data.userProfile);
    }
    if (data.tokenObj?.idToken) {
      this.storageService.local.setItem(keys.IdToken, data.tokenObj.idToken);
      this.storageService.local.setItem((keys as any).AccessToken, data.tokenObj.accessToken);
      this.storageService.local.setItem((keys as any).RefreshToken, data.tokenObj.refreshToken);
    }
  }

  private clearLoginData(): void {
    const keys = this.storageService.keys;
    this.storageService.local.removeItem(keys.UserProfile);
    this.storageService.local.removeItem(keys.IdToken);
    this.storageService.local.removeItem((keys as any).AccessToken);
    this.storageService.local.removeItem((keys as any).RefreshToken);
    
    this.storageService.session.removeItem(keys.UserProfile);
    this.storageService.session.removeItem(keys.IdToken);
    this.storageService.session.removeItem((keys as any).AccessToken);
    this.storageService.session.removeItem((keys as any).RefreshToken);
  }

  private initStaffData(userProfile: UserProfile): Observable<boolean> {
    const permission$ = this.apiRbacService.doGet<UserPermission>(
      `/Permission/User/${userProfile.userNo}`
    );
    return permission$.pipe(
      switchMap((userPermissionResponse) => {
        if (
          userPermissionResponse &&
          userPermissionResponse.rolePermissions &&
          userPermissionResponse.rolePermissions.length > 0
        ) {
          const systemPermissions = userPermissionResponse.rolePermissions
            .map((item) => item.systemPermissions)
            .reduce((acc, item) => {
              acc.push(...item);
              return acc;
            }, []);
          this.userProfileService.currentSystemPermissionsSubject.next(
            systemPermissions
          );
          return of(true);
        } else {
          return of(false);
        }
      }),
      catchError(() => of(false))
    );
  }

  private initOutsiderData(userProfile: UserProfile): Observable<boolean> {
    // TODO: 取得權限資料，目前沒有權限資料可取得
    this.userProfileService.currentSystemPermissionsSubject.next([
      {
        systemCode: 'Web Submission',
        systemName: 'Web Submission',
        functionCode: 'Submission',
        functionName: 'Submission',
        privilegeCode: 'Add',
        privilegeName: 'Add',
      },
      {
        systemCode: 'Web Submission',
        systemName: 'Web Submission',
        functionCode: 'Announcement',
        functionName: 'Announcement',
        privilegeCode: 'Enquire',
        privilegeName: 'Enquire',
      },
      {
        systemCode: 'Web Submission',
        systemName: 'Web Submission',
        functionCode: 'AnnouncementSetting',
        functionName: 'Announcement Setting',
        privilegeCode: 'Add',
        privilegeName: 'Add',
      },
      {
        systemCode: 'Web Submission',
        systemName: 'Web Submission',
        functionCode: 'AnnouncementSetting',
        functionName: 'Announcement Setting',
        privilegeCode: 'Enquire',
        privilegeName: 'Enquire',
      },
      {
        systemCode: 'Web Submission',
        systemName: 'Web Submission',
        functionCode: 'AnnouncementSetting',
        functionName: 'Announcement Setting',
        privilegeCode: 'Edit',
        privilegeName: 'Edit',
      },
    ]);
    return of(true).pipe(delay(500));
  }

  initLoginUserData(userProfile: UserProfile): Observable<boolean> {
    // Sales, Dealer, Agent 都能從外網登入
    const outsiderRoleConditions = [0, 1, 2];
    if (
      userProfile.extranetUserRole !== undefined &&
      userProfile.extranetUserRole !== null
    ) {
      this.userProfileService.currentUserSubject.next(userProfile);
      // 判斷登入者角色，取得對應的初始資料
      if (outsiderRoleConditions.includes(userProfile.extranetUserRole)) {
        return this.initOutsiderData(userProfile);
      } else {
        return this.initStaffData(userProfile);
      }
    } else {
      return of(false);
    }
  }

  login(loginRequest: {
    account: string;
    password: string;
    recaptchaToken?: string;
  }): Observable<{ isSuccess: boolean; msg?: string; rtnCode?: number }> {
    const subject = new Subject<{
      isSuccess: boolean;
      msg?: string;
      rtnCode?: number;
    }>();

    this.apiWebSubmissionService
      .doPost<ApiResponse<AdLoginData>>('/Login/ADLogin', {
        account: loginRequest.account,
        password: loginRequest.password,
        recaptchaToken: loginRequest.recaptchaToken,
      })
      .subscribe({
        next: async (result) => {
          if (result && result.rtnCode === 0 && result.data) {
            const {
              data: { userInfo, idToken, accessToken, refreshToken, useRole },
            } = result;

            const userProfile: UserProfile = {
              userNo: userInfo.userNo,
              userName: userInfo.userName,
              userChineseName: '', // TODO: 登入api未提供
              email: userInfo.email,
              gender: true, // TODO: 登入api未提供
              enable: userInfo.enable,
              userJobTitles: [],
              managerUserNo: userInfo.managerUserNo,
              managerUserName: userInfo.managerUserNo, // TODO: 登入api未提供
              jobTitleId: userInfo.jobTitleId.toString(),
              workType: userInfo.workType,
              departmentId: userInfo.departmentId,
              departmentCode: userInfo.departmentCode,
              departmentName: userInfo.departmentName,
              companyId: userInfo.companyId,
              companyCode: userInfo.companyCode,
              companyShortName: '', // TODO: 登入api未提供
              companyName: '', // TODO: 登入api未提供
              extranetUserRole: useRole,
              dealerCode: userInfo?.dealerCode || '',
              dealerName: userInfo?.dealerName || '',
              agentCode: userInfo?.agentCode || '',
              agentName: userInfo?.agentName || '',
            };
           
            this.saveLoginData({ userProfile, tokenObj:{
                idToken:idToken,
                accessToken: accessToken,
                refreshToken: refreshToken
            }});

            this.initLoginUserData(userProfile).subscribe((isSuccess) => {
              let msg = '';
              if (!isSuccess) {
                this.clearLoginData();
                msg = 'Login fail';
              }
              subject.next({
                isSuccess,
                msg,
              });
            });

            //若登入者為業務，添加(覆蓋) userJobTitles
            if (useRole == 0) {
              let url = `/UserProfile/${userInfo.userNo}`;
              this.apiRbacService.setBaseUrl(this.appConfig.getApiUrl('rbac'));
              const jobTitlesData = await this.apiRbacService
                .doGet<UserProfile>(url, new HttpParams(), {
                  'X-ReturnError': 'true',
                })
                .toPromise()
                .catch((err) => {});

              if (jobTitlesData) {
                userProfile.userJobTitles = jobTitlesData.userJobTitles ?? [];
                userProfile.isSalesSupport = jobTitlesData.isSalesSupport;
                this.saveLoginData({ userProfile, tokenObj:{
                  idToken:idToken,
                  accessToken: accessToken,
                  refreshToken: refreshToken
                }});
              }
            }
            //.......... End 若登入者為業務，添加(覆蓋) userJobTitles
          } else {
            subject.next({
              isSuccess: false,
              msg: result.msg,
              rtnCode: result.rtnCode,
            });
            subject.complete();
          }
        },
        error: (error) => {
          console.error('loginAd fail fail. \n reason: ', error);
          subject.next({ isSuccess: false, msg: error });
          subject.complete();
        },
      });
    return subject;
  }

  logout(): void {
    this.userProfileService.currentUserSubject.next(null);
    this.userProfileService.currentSystemPermissionsSubject.next([]);
    this.clearLoginData();
  }

  redirectLogin(
    email: string,
    crossDomainSecret: string
  ): Observable<{ isSuccess: boolean; msg?: string; rtnCode?: number }> {
    const subject = new Subject<{
      isSuccess: boolean;
      msg?: string;
      rtnCode?: number;
    }>();

    const loginUrl = `${this.authUrl}/Login/TokenLogin`;
    const req = {
      email,
      crossDomainSecret,
    };

    this.http.post<any>(loginUrl, req).subscribe({
      next: async (result) => {
        if (result && result.rtnCode === 0 && result.data) {
          const {
            data: { userInfo, idToken, accessToken, refreshToken, useRole },
          } = result;

          const userProfile: UserProfile = {
            userNo: userInfo.userNo,
            userName: userInfo.userName,
            userChineseName: '', // TODO: 登入api未提供
            email: userInfo.email,
            gender: true, // TODO: 登入api未提供
            enable: userInfo.enable,
            userJobTitles: [],
            managerUserNo: userInfo.managerUserNo,
            managerUserName: userInfo.managerUserNo, // TODO: 登入api未提供
            jobTitleId: userInfo.jobTitleId.toString(),
            workType: userInfo.workType,
            departmentId: userInfo.departmentId,
            departmentCode: userInfo.departmentCode,
            departmentName: userInfo.departmentName,
            companyId: userInfo.companyId,
            companyCode: userInfo.companyCode,
            companyShortName: '', // TODO: 登入api未提供
            companyName: '', // TODO: 登入api未提供
            extranetUserRole: useRole,
            dealerCode: userInfo?.dealerCode || '',
            dealerName: userInfo?.dealerName || '',
            agentCode: userInfo?.agentCode || '',
            agentName: userInfo?.agentName || '',
          };

          this.saveLoginData({ userProfile, tokenObj:{
              idToken:idToken,
              accessToken: accessToken,
              refreshToken: refreshToken
          }});

          this.initLoginUserData(userProfile).subscribe((isSuccess) => {
            let msg = '';
            if (!isSuccess) {
              this.clearLoginData();
              msg = 'Login fail';
            }
            subject.next({
              isSuccess,
              msg,
            });
          });

          //若登入者為業務，添加(覆蓋) userJobTitles
          if (useRole == 0) {
            let url = `/UserProfile/${userInfo.userNo}`;
            const jobTitlesData = await this.apiRbacService
              .doGet<UserProfile>(url, new HttpParams(), {
                'X-ReturnError': 'true',
              })
              .toPromise()
              .catch((err) => {});

            if (jobTitlesData) {
              userProfile.userJobTitles = jobTitlesData.userJobTitles ?? [];
              this.saveLoginData({ userProfile,  tokenObj:{
                idToken:idToken,
                accessToken: accessToken,
                refreshToken: refreshToken
              }});
            }
          }
          //.......... End 若登入者為業務，添加(覆蓋) userJobTitles
        } else {
          subject.next({
            isSuccess: false,
            msg: result.msg,
            rtnCode: result.rtnCode,
          });
          subject.complete();
        }
      },
      error: (error) => {
        console.error('loginAd fail fail. \n reason: ', error);
        subject.next({ isSuccess: false, msg: error });
        subject.complete();
      },
    });
    return subject;
  }

  /**
   * @description 檢查 Id Token 是否過期 (進件外網)
   */
  checkIdTokenExpiration(): boolean {
    const idToken =
      this.storageService.local.getItem(this.storageService.keys.IdToken) ??
      this.storageService.session.getItem(this.storageService.keys.IdToken);

    if (!idToken) {
      return true;
    }

    const now = new Date();
    const base64Url = idToken.split('.')[1];
    const base64 = base64Url
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .replace(/\s/g, '');
    const jwtPayload = JSON.parse(decodeURIComponent(window.atob(base64)));
    const expiryTime = subMinutes(fromUnixTime(jwtPayload.exp), 5);

    return now > expiryTime;
  }

  get idToken(): string {
    const idToken =
      this.storageService.local.getItem(this.storageService.keys.IdToken) ??
      this.storageService.session.getItem(this.storageService.keys.IdToken);

    return idToken;
  }

  get accessToken(): string {
    const accessToken =
      this.storageService.local.getItem((this.storageService.keys as any).AccessToken) ??
      this.storageService.session.getItem((this.storageService.keys as any).AccessToken);

    return accessToken;
  }

  get refreshToken(): string {
    const refreshTokenS =
      this.storageService.local.getItem((this.storageService.keys as any).RefreshToken) ??
      this.storageService.session.getItem((this.storageService.keys as any).RefreshToken);

    return refreshTokenS;
  }

  crossDomainLogin(idToken: string): Observable<string> {   
    
    // const loginUrl = `${this.authUrl}/Login/CrossDomainLogin`;
    // const req = {
    //   idToken,
    // };
    // return this.http.post<ApiResponse<any>>(loginUrl, req).pipe(
    //   map((resp: any) => {
    //     const apiResult = deepCopy(resp);
    //     if (apiResult.rtnCode !== 0) {
    //       return throwError(apiResult.msg);
    //     }
    //     return apiResult.data?.crossDomainKey ?? '';
    //   })
    // );

    return this.refreshIdToken().pipe(
      mergeMap(()=>{ //跨域前要先刷新 token ，先打 API 刷新 storage 的 IdToken/RefreshToken/AccessToken
        return of(null);
      }),
      mergeMap(()=>{
        const loginUrl = `${this.authUrl}/Login/CrossDomainLogin`;
        const req = {
          idToken: this.idToken,//拿取被刷新的 idToken
          refreshToken: this.refreshToken,
        };
        return this.http.post<ApiResponse<any>>(loginUrl, req).pipe(
          map((resp: any) => {
            const apiResult = deepCopy(resp);
            if (apiResult.rtnCode !== 0) {
              return throwError(apiResult.msg);
            }
            return apiResult.data?.crossDomainKey ?? '';
          })
        ); 
      }),
    );    
  }

  //拿 refreshToken 去更新 IdToken/RefreshToken/AccessToken
  refreshIdToken(): Observable<any>{ 
    const refreshTokenUrl = `${this.authUrl}/Login/RefreshToken`;
    const req = {
      refreshToken: this.refreshToken,
      useRole: this.storageService.local.getItem(this.storageService.keys.UserProfile).extranetUserRole
    };
   
    return this.http.post<ApiResponse<any>>(refreshTokenUrl, req).pipe(
      map((resp: any) => { 
        if(resp.rtnCode === 0){
          const keys = this.storageService.keys;
          this.storageService.local.setItem(keys.IdToken, resp?.data?.idToken);
          this.storageService.local.setItem((keys as any).RefreshToken, resp?.data?.refreshToken);
          this.storageService.local.setItem((keys as any).AccessToken, resp?.data?.accessToken);
        }
      })
    );
  }

  
}


